<template>
  <div>
    <LoginScreen v-if="!isLoggedIn" @login="handleLogin" :companyInformation="companyInformation" />
    <div v-else class="app">
      <!--img v-if="logo" :src="logo" alt="Company logo" />
      <p v-if="loading">Loading...</p>
      <p v-if="error">{{ error }}</p-->
      <!-- <p v-if="conversations">{{ conversations }}</p> -->
      <AppSidebar :conversations="conversations" :conversation-messages-map="conversationMessagesMap" :companyInformation="companyInformation" :currentUser="currentUser" @load-conversation="loadConversation" @new-chat="resetToNewChat"
        @clear-conversation="clearConversation" @refresh-conversations="loadConversations" @logout="handleLogout"
        @collapsed="handleSidebarCollapsed" @update-logo="updateLogo" @show-memories="showMemoryScreen"
        @show-knowledge="showKnowledgeScreen"
        @new-chat-in-project="startNewChatInProject"
        @show-project-view="showProjectView"
        @show-project-artifacts="showProjectArtifacts"/>
      <div class="main-content">
        <!-- Memory Management Screen -->
        <MemoryManagement v-if="showMemories" :companyInformation="companyInformation" @close="showMemories = false" />
        <!-- Knowledge Management Screen (Admin) -->
        <KnowledgeManagement v-else-if="showKnowledge" @close="showKnowledge = false" />
        <!-- Project View -->
        <ProjectView
          v-else-if="currentProject"
          :key="currentProject.id"
          :project="currentProject"
          :allConversations="conversations"
          @close-project="closeProjectView"
          @new-chat-in-project="startNewChatInProject"
          @open-conversation="openConversationFromProject"
          @delete-conversation="clearConversation"
        />
        <!-- Normal Chat View -->
        <template v-else>
          <div :class="['dropdown-container', collapsed ? 'collapsed-dropdown' : ' ']">
            <ChatbotDropdown :chatbots="chatbots" :selectedChatbot="selectedChatbot" @select-chatbot="selectChatbot" />
            <span v-if="(currentConversation && currentConversation.project_name) || pendingProjectName" class="project-indicator">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
              </svg>
              {{ currentConversation?.project_name || pendingProjectName }}
            </span>
          </div>
          <div class="header-container" v-if="!currentConversation || (currentConversation && currentMessages && currentMessages.length === 0)">
            <ChatHeader
              :logo="logo" :title="selectedChatbot ? selectedChatbot.model_title : ''" :companyInformation="companyInformation" />
          </div>
          <ChatMain v-if="!currentConversation"
            :selectedChatbot="selectedChatbot" @select-prompt="startConversationWithPrompt" />
          <ChatConversation v-else :conversations="currentConversation" :messages="currentMessages" :companyInformation="companyInformation" :isThinking="isThinking" :selectedChatbot="selectedChatbot" :autoTtsOutput="autoTtsOutput" @submit-feedback="submitFeedback" />
          <!-- Stop generating button (visible during streaming) -->
          <div v-if="isStreaming" class="stop-generating-container">
            <button class="stop-generating-btn" @click="stopStreaming" title="Stop generating">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="3" y="3" width="10" height="10" rx="1" />
              </svg>
              Stop generating
            </button>
          </div>
          <ChatFooter v-if="selectedChatbot && selectedChatbot.name" @send-message="sendMessage"
            @load-conversation="loadConversation" @auto-tts-changed="onAutoTtsChanged" :isThinking="isThinking" ref="chatFooter" :companyInformation="companyInformation" :collapsed="collapsed" :selectedChatbot="selectedChatbot" />
        </template>
      </div>
      <!-- Artifacts toggle button (visible when a conversation is active) -->
      <button
        v-if="currentConversation && !showMemories && !showKnowledge"
        class="artifacts-toggle-btn"
        :class="{ active: showArtifactsPane }"
        @click="toggleConversationArtifacts"
        title="Toggle artifacts pane"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      </button>
      <!-- Artifacts Slide-out Pane -->
      <ArtifactsPane
        :isOpen="showArtifactsPane"
        :conversationId="currentConversation ? currentConversation.resource_id : null"
        :projectId="artifactsPaneProjectId"
        :mode="artifactsPaneMode"
        @close="showArtifactsPane = false"
      />
    </div>
  </div>
</template>
 
<script>
import LoginScreen from './components/LoginScreen.vue';
import AppSidebar from './components/AppSidebar.vue';
import ChatHeader from './components/ChatHeader.vue';
//import ChatMain from './components/ChatMain.vue';
import ChatConversation from './components/ChatConversation.vue';
import ChatFooter from './components/ChatFooter.vue';
import ChatbotDropdown from './components/ChatbotDropdown.vue';
import MemoryManagement from './components/MemoryManagement.vue';
import KnowledgeManagement from './components/KnowledgeManagement.vue'; // eslint-disable-line no-unused-vars
import ArtifactsPane from './components/ArtifactsPane.vue';
import ProjectView from './components/ProjectView.vue';
import axios from '@/axios-config';
import { initTheme, getCurrentTheme } from '@/utils/themeManager';
 
export default {
  components: {
    LoginScreen,
    AppSidebar,
    ChatHeader,
    //ChatMain,
    ChatConversation,
    ChatFooter,
    ChatbotDropdown,
    MemoryManagement,
    KnowledgeManagement,
    ArtifactsPane,
    ProjectView
  },
 
  data() {
    return {
      currentConversation: null,
      currentMessages: [],
      conversations: [],
      chatbots: [],
      selectedChatbot: null,
      isNewConversation: true,
      isLoggedIn: false, // Track the login state
      newMessage: '',
      currentUser: null,
      isThinking: false,
      isStreaming: false,
      streamController: null,  // AbortController for cancelling streams
      collapsed: false,
      conversationMessagesMap: {},
      logo: null,      // reference to logo throughout site
      companyInformation: {},
      loading: true,
      error: null,
      showMemories: false,
      showKnowledge: false,
      showArtifactsPane: false,
      artifactsPaneMode: 'conversation',
      artifactsPaneProjectId: null,
      pendingProjectId: null,
      pendingProjectName: null,
      autoTtsOutput: false,
      currentProject: null,
    };
  },
  created() {
    // Initialize theme immediately with default settings
    // This ensures the theme is set before any components render
    initTheme();

    this.checkAuthStatus();
    this.fetchCompanySettings();

    // DEBUG: log CSRF_TRUSTED_ORIGINS
    fetch('/api/debug/csrf-origins/').then(r => r.json()).then(d => console.log('CSRF_TRUSTED_ORIGINS:', d.CSRF_TRUSTED_ORIGINS)).catch(e => console.error('Failed to fetch CSRF origins:', e));
  },
 
  computed: {
    filteredConversations() {
      return this.conversations.filter(convo => convo.chatbot === this.selectedChatbot);
    }
  },
  methods: {
    async checkAuthStatus() {
      try {
        const response = await axios.get('/api/auth/status', { withCredentials: true });

        if (response.data.isAuthenticated) {
          this.currentUser = response.data.user;
          this.isLoggedIn = true;
          console.log('User is logged in:', response.data.user);

          // Load user-specific data after confirming authentication
          this.loadChatbots();
          this.loadConversations();
          this.pickDefaultChatbot();
        } else {
          console.log('User not logged in');
          this.isLoggedIn = false;
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        this.isLoggedIn = false;
      }
    },

    async fetchCompanySettings() {
      this.loading = true;
      this.error = null;
      try {
        const companyInfoResponse = await axios.get('/api/company-setting/');
        this.companyInformation = companyInfoResponse.data;
        this.logo = this.companyInformation.logo || this.logo;

        if(getCurrentTheme().includes('dark'))
        {
          this.logo = this.companyInformation.logo_dark_mode;
        }

        // Initialize theme system with theme family from backend
        const themeFamily = this.companyInformation.theme_family;
        initTheme(themeFamily);

        // Update favicon dynamically
        this.updateHtmlHeader();
      } catch (err) {
        console.error(err);
        this.error = 'Failed to load company settings';
        // Initialize with default theme if fetch fails
        initTheme();
      } finally {
        this.loading = false;
      }
    },
    updateHtmlHeader() {
      const favicon = document.getElementById('favicon');
      const siteTitle = document.getElementById('siteTitle');
      if (favicon && this.companyInformation.favicon) {
        favicon.href = this.companyInformation.favicon;
      }
      if(siteTitle && this.companyInformation.product_name)
      {
        siteTitle.innerHTML = this.companyInformation.product_name;
      }
    },
    updateLogo(){
      if(getCurrentTheme().includes('dark'))
      {
        this.logo = this.companyInformation.logo_dark_mode; 
      }
      else
      {
        this.logo = this.companyInformation.logo;
      }
    },
    pickDefaultChatbot() {
      if (!this.chatbots.length || !this.currentUser) return;   // need both

      // First, check if there's a stored chatbot in localStorage
      const storedChatbot = localStorage.getItem('selectedChatbot');
      if (storedChatbot) {
        try {
          const chatbot = JSON.parse(storedChatbot);
          // Verify the chatbot still exists in the available chatbots
          const exists = this.chatbots.find(c => c.id === chatbot.id);
          if (exists) {
            this.selectedChatbot = exists;
            return; // Use the stored preference and exit early
          }
        } catch (error) {
          console.error('Error parsing stored chatbot:', error);
        }
      }

      // If no valid stored chatbot, use email-based logic
      const wantsFFGA = this.currentUser.email?.toLowerCase().endsWith('@ffga.com');

      // Look for bots by botid instead of hardcoded IDs
      let targetBotId;
      if (wantsFFGA) {
        targetBotId = 'ffga';
      } else {
        targetBotId = 'camgpt';
      }

      const bot = this.chatbots.find(b => b.botid === targetBotId);
      if (bot) {
        this.selectedChatbot = bot;
      } else {
        // Fallback to first available bot
        //this.selectedChatbot = this.chatbots[0];
      }
    },
    handleSidebarCollapsed(isCollapsed) {
      this.collapsed = isCollapsed;
    },
    showMemoryScreen() {
      this.showMemories = true;
      this.showKnowledge = false;
    },
    showKnowledgeScreen() {
      this.showKnowledge = true;
      this.showMemories = false;
    },
    toggleConversationArtifacts() {
      if (this.showArtifactsPane && this.artifactsPaneMode === 'conversation') {
        this.showArtifactsPane = false;
      } else {
        this.artifactsPaneMode = 'conversation';
        this.artifactsPaneProjectId = null;
        this.showArtifactsPane = true;
      }
    },
    showProjectArtifacts(project) {
      this.artifactsPaneMode = 'project';
      this.artifactsPaneProjectId = project.id;
      this.showArtifactsPane = true;
    },
    showProjectView(project) {
      this.currentProject = project;
      this.currentConversation = null;
      this.currentMessages = [];
      this.showMemories = false;
      this.showKnowledge = false;
    },
    closeProjectView() {
      this.currentProject = null;
    },
    openConversationFromProject(conversation) {
      this.currentProject = null;
      this.loadConversation(conversation);
    },


    async loadConversations() {
      try {
        const response = await axios.get('/api/conversation/', { withCredentials: true });
        this.conversations = response.data;
 
        // Fetch messages for each conversation
        const messagesMap = {};
        const messagePromises = this.conversations.map(async (conv) => {
          try {
            const res = await axios.get(`/api/conversation/${conv.resource_id}/messages/`);
            messagesMap[conv.resource_id] = res.data;
          }
          catch (err) {
            console.err(`Failed to load messages for ${conv.resource_id}`, err)
            messagesMap[conv.resource_id] = [];
          }
        });
 
        await Promise.all(messagePromises);
        this.conversationMessagesMap = messagesMap;
 
        this.pickDefaultChatbot();
      } catch (error) {
        console.error("Error loading conversations:", error);
      }
    },
 
    async loadChatbots() {
      try {
        const response = await axios.get('/api/assistant/');
        this.chatbots = response.data;
        this.pickDefaultChatbot();
      } catch (error) {
        console.error("Error loading chatbots:", error);
      }
    },
    resetToNewChat() {
      this.pickDefaultChatbot();
      this.currentConversation = null;
      this.isNewConversation = true;
      this.pendingProjectId = null;
      this.pendingProjectName = null;
      this.currentMessages = [];
      this.showMemories = false;
      this.showKnowledge = false;
    },
    startNewChatInProject(project) {
      console.log('startNewChatInProject: project.id =', project.id, 'project.name =', project.name);
      this.pickDefaultChatbot();
      this.currentConversation = null;
      this.isNewConversation = true;
      this.pendingProjectId = project.id;
      this.pendingProjectName = project.name;
      this.currentMessages = [];
      // Close project view and overlays to show chat interface
      this.currentProject = null;
      this.showMemories = false;
      this.showKnowledge = false;
    },
 
    loadConversation(conversation) {
      console.log('loadConversation:', conversation.id, 'resource_id:', conversation.resource_id, 'project:', conversation.project);
      this.currentMessages = [];
      this.currentConversation = conversation;
      this.isNewConversation = false;
      this.currentProject = null;
      this.showMemories = false;
      this.showKnowledge = false;

      // Find the matching chatbot by ID
      const chatbot = this.chatbots.find(bot => bot.id === conversation.ai_assistant);
      if (chatbot) this.selectedChatbot = chatbot;

      axios.get(`/api/conversation/${conversation.resource_id}/messages/`)
        .then(response => {
          this.currentMessages = Array.isArray(response.data) ? response.data : [];
          console.log('loadConversation: loaded', this.currentMessages.length, 'messages');
        })
        .catch(error => {
          console.error("Error loading conversation messages:", error);
          if (!this.currentMessages) {
            this.currentMessages = [];
          }
        });
    },
 
    async startNewChat(message, file, filePreview) {
      console.log('Attempting to send API request...');
      // Capture project ID before any async operations to avoid race conditions
      const projectId = this.pendingProjectId;
      const res = await axios.get('/api/get-csrf-token');
      const token = res.data.csrfToken;
      try {
        const requestData = {
            assistant: this.selectedChatbot,
            user_message: message
        };
        // Include project_id if starting a chat within a project
        if (projectId) {
            requestData.project_id = projectId;
            console.log('startNewChat: Including project_id =', projectId);
        }
        const response = await axios.post(
          '/api/newconvo/',
          requestData, {
          headers: {
            'X-CSRFToken': token
          }
        }
        );
        this.currentConversation = response.data;
        console.log('startNewChat: Response project =', response.data.project, 'project_name =', response.data.project_name);
        // Warn if project was requested but not set in response
        if (projectId && !response.data.project) {
          console.warn('startNewChat: project_id was sent but response has no project association!');
        }
        // Clear the pending project after conversation is created
        this.pendingProjectId = null;
        this.pendingProjectName = null;
        const userMsg = { role: 'user', content: message };
        if (file) {
          const isImage = file.type.startsWith('image/');
          userMsg.attachment = {
            url: filePreview || URL.createObjectURL(file),
            filename: file.name,
            type: isImage ? 'image' : 'document'
          };
        }
        this.currentMessages.push(userMsg);
        this.isNewConversation = false;
 
        return this.currentConversation
      } catch (error) {
        console.error("Error starting new conversation:", error);
      }
    },
 
 
    async clearConversation(conversationId) {
      const conversation = this.conversations.find(convo => convo.id === conversationId);
      this.conversations = this.conversations.filter(convo => convo.id !== conversationId);
      const url = `/api/clearconvo/${conversation.resource_id}/`;
      console.log('Attempting to send API request...');
      const res = await axios.get('/api/get-csrf-token');
      const token = res.data.csrfToken;
      axios.post(url, null, {
        headers: {
          'X-CSRFToken': token
        },
      });
      if (this.currentConversation && this.currentConversation.id === conversationId) {
        this.currentConversation = null;
        this.isNewConversation = true;
      }
      this.currentMessages = [];
    },
 
    async clearcurrentMessages() {
      this.currentMessages = []
    },
 
    async sendMessage(payload) {
      // payload is either a plain string or { message, file }
      const hasFile = typeof payload === 'object' && payload.file;
      const message = hasFile ? payload.message : payload;
      const file = hasFile ? payload.file : null;
      const filePreview = hasFile ? payload.preview : null;

      // show the thinking bubble
      this.isThinking = true
      const res = await axios.get('/api/get-csrf-token');
      const token = res.data.csrfToken;
      if (!this.currentConversation || this.isNewConversation) {

        const conv = await this.startNewChat(message, file, filePreview);

        if (!conv) {
          console.error("Failed to create a new conversation.");
          this.isThinking=false;
          return;
        }
        this.currentConversation = conv;

        if (!this.conversations.some(
          convo => convo.resource_id === this.currentConversation.resource_id
        )) {
          this.conversations.unshift(this.currentConversation);

        }
      } else {
        // Build user message with attachment preview for immediate display
        const userMsg = { role: 'user', content: message };
        if (file) {
          const isImage = file.type.startsWith('image/');
          userMsg.attachment = {
            url: filePreview || URL.createObjectURL(file),
            filename: file.name,
            type: isImage ? 'image' : 'document'
          };
        }
        this.currentMessages.push(userMsg);
        if (!this.currentMessages) {
          this.currentMessages = [];
        }


      }
      // Call the API for the AI response
      const conversationId = this.currentConversation.resource_id;


      const botId = this.selectedChatbot ? this.selectedChatbot.id : undefined;
      const botType = this.selectedChatbot ? this.selectedChatbot.botid : undefined;

      // Debug logging
      console.log('Selected chatbot:', this.selectedChatbot);
      console.log('Bot ID:', botId, 'Bot Type:', botType);

      // Detect if the selected chatbot is an image generation model
      const isImageModel = this.selectedChatbot && (
        (this.selectedChatbot.extra_config && this.selectedChatbot.extra_config.image_generation) ||
        ['dall-e', 'dalle', 'gpt-image', 'image'].some(kw =>
          (this.selectedChatbot.model_name || '').toLowerCase().includes(kw)
        )
      );

      // Use unified endpoint for file uploads and image generation models;
      // use streaming for text-only messages with non-image models
      if (file || isImageModel) {
        // File upload or image generation: use non-streaming unified endpoint
        const apiUrl = '/api/unified-assistant-response/';
        try {
          const formData = new FormData();
          formData.append('conversation_id', conversationId);
          formData.append('botid', botType);
          formData.append('user_message', message || '');
          if (file) {
            formData.append('file', file);
            console.log('[CamGPT] Uploading file:', file.name, 'size:', file.size, 'type:', file.type);
          }
          const response = await axios.post(apiUrl, formData, {
            headers: {
              'X-CSRFToken': token,
              'Content-Type': 'multipart/form-data',
            }
          });
          this.isThinking = false;

          // Log debug info to browser console for troubleshooting file uploads
          if (response.data._file_debug) {
            console.log('[CamGPT] File upload debug:', response.data._file_debug);
          }
          if (response.data._debug) {
            console.log('[CamGPT] Provider debug:', response.data._debug);
          }

          this.currentMessages.push({
            role: 'assistant',
            html_content: response.data.reply,
            resource_id: response.data.message_resource_id,
            feedback_vote: null,
          });
          this.$refs.chatFooter.enableInput();
        } catch (error) {
          console.error("Error fetching AI response:", error);
          this.isThinking = false;

          // Extract meaningful error message from backend response
          let errorDetail = '';
          if (error.response && error.response.data) {
            errorDetail = error.response.data.error || error.response.data.detail || '';
          }

          // Build user-friendly inline error message with styling
          let userMessage = '';
          if (isImageModel) {
            const detail = errorDetail ? `<p style="margin-top:6px;font-size:13px;opacity:0.85;">${errorDetail}</p>` : '';
            userMessage = `<div class="inline-error-message"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:2px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><div><p style="margin:0;font-weight:500;">Image generation failed</p><p style="margin:4px 0 0;font-size:13px;">The image could not be generated. Please try again or rephrase your prompt.</p>${detail}</div></div>`;
          } else {
            const detail = errorDetail ? `<p style="margin-top:6px;font-size:13px;opacity:0.85;">${errorDetail}</p>` : '';
            userMessage = `<div class="inline-error-message"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:2px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><div><p style="margin:0;font-weight:500;">Something went wrong</p><p style="margin:4px 0 0;font-size:13px;">Failed to get a response. Please try again.</p>${detail}</div></div>`;
          }

          this.currentMessages.push({
            role: 'assistant',
            html_content: userMessage,
          });
        } finally {
          if (this.$refs.chatFooter && this.$refs.chatFooter.enableInput) {
            this.$refs.chatFooter.enableInput();
          }
        }
      } else if (this.selectedChatbot && this.selectedChatbot.supports_stream === false) {
        // Model does not support streaming: fall back to non-streaming unified endpoint
        const apiUrl = '/api/unified-assistant-response/';
        try {
          const formData = new FormData();
          formData.append('conversation_id', conversationId);
          formData.append('botid', botType);
          formData.append('user_message', message || '');
          const response = await axios.post(apiUrl, formData, {
            headers: {
              'X-CSRFToken': token,
              'Content-Type': 'multipart/form-data',
            }
          });
          this.isThinking = false;
          this.currentMessages.push({
            role: 'assistant',
            html_content: response.data.reply,
            resource_id: response.data.message_resource_id,
            feedback_vote: null,
          });
          this.$refs.chatFooter.enableInput();
        } catch (error) {
          console.error("Error fetching AI response (non-streaming fallback):", error);
          this.isThinking = false;

          let errorDetail = '';
          if (error.response && error.response.data) {
            errorDetail = error.response.data.error || error.response.data.detail || '';
          }
          const detail = errorDetail ? `<p style="margin-top:6px;font-size:13px;opacity:0.85;">${errorDetail}</p>` : '';
          const userMessage = `<div class="inline-error-message"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:2px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><div><p style="margin:0;font-weight:500;">Something went wrong</p><p style="margin:4px 0 0;font-size:13px;">Failed to get a response. Please try again.</p>${detail}</div></div>`;

          this.currentMessages.push({
            role: 'assistant',
            html_content: userMessage,
          });
        } finally {
          if (this.$refs.chatFooter && this.$refs.chatFooter.enableInput) {
            this.$refs.chatFooter.enableInput();
          }
        }
      } else {
        // Text-only with streaming-capable model: use SSE streaming endpoint
        await this.sendMessageStreaming(conversationId, botType, message, token);
      }
  },

    async sendMessageStreaming(conversationId, botType, message, csrfToken) {
      // Push a placeholder assistant message for streaming
      const streamingMsg = {
        role: 'assistant',
        content: '',
        html_content: '',
        streaming: true,
        resource_id: null,
        feedback_vote: null,
      };
      this.currentMessages.push(streamingMsg);
      const msgIndex = this.currentMessages.length - 1;

      this.isThinking = false;
      this.isStreaming = true;

      // Create an AbortController so the user can cancel
      this.streamController = new AbortController();
      let fullText = '';
      let displayedLen = 0;
      let smoothInterval = null;

      // Smooth streaming: reveal text at an adaptive rate via setInterval.
      // When data arrives in network bursts the animation spreads the reveal across
      // frames so the user sees a steady typing effect instead of sudden jumps.
      // Uses setInterval (not requestAnimationFrame) because rAF pauses when the
      // tab loses focus and can be unreliable behind reverse proxies.
      smoothInterval = setInterval(() => {
        const buffered = fullText.length - displayedLen;
        if (buffered > 0) {
          // Adaptive step: small buffer → slow reveal, large buffer → fast catch-up
          const step = Math.max(1, Math.ceil(buffered / 8));
          displayedLen = Math.min(displayedLen + step, fullText.length);
          if (this.currentMessages[msgIndex]) {
            this.currentMessages[msgIndex].content = fullText.substring(0, displayedLen);
          }
        }
      }, 16);

      try {
        const response = await fetch('/api/stream/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            botid: botType,
            user_message: message,
            conversation_id: conversationId,
          }),
          signal: this.streamController.signal,
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Stream error (${response.status}): ${text}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        fullText = '';

        let reading = true;
        while (reading) {
          const { done, value } = await reader.read();
          if (done) { reading = false; break; }

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete line in buffer

          let currentEventType = null;
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEventType = line.substring(7).trim();
            } else if (line.startsWith('data: ')) {
              const data = line.substring(6);

              if (currentEventType === 'token') {
                // Unescape SSE-encoded newlines
                const token = data.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
                fullText += token;
                // Animation loop handles smooth text reveal
                currentEventType = null;
              } else if (currentEventType === 'done') {
                if (smoothInterval) { clearInterval(smoothInterval); smoothInterval = null; }
                try {
                  const doneData = JSON.parse(data);
                  // Replace streaming content with final HTML
                  this.currentMessages[msgIndex].html_content = doneData.html_content;
                  this.currentMessages[msgIndex].content = fullText;
                  this.currentMessages[msgIndex].resource_id = doneData.message_resource_id;
                  this.currentMessages[msgIndex].streaming = false;
                } catch (e) {
                  console.error('Error parsing done event:', e);
                }
                currentEventType = null;
              } else if (currentEventType === 'status') {
                // Status update (e.g., "Generating documents...")
                this.currentMessages[msgIndex].statusMessage = data;
                currentEventType = null;
              } else if (currentEventType === 'error') {
                if (smoothInterval) { clearInterval(smoothInterval); smoothInterval = null; }
                console.error('Stream error event:', data);
                // Preserve partial text and append styled inline error
                const partialText = fullText ? `<div class="markdown-body" style="margin-bottom:10px;">${fullText}</div>` : '';
                const errorHtml = `${partialText}<div class="inline-error-message"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:2px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><div><p style="margin:0;font-weight:500;">Streaming error</p><p style="margin:4px 0 0;font-size:13px;">${data}</p></div></div>`;
                this.currentMessages[msgIndex].html_content = errorHtml;
                this.currentMessages[msgIndex].content = fullText || '';
                this.currentMessages[msgIndex].streaming = false;
                currentEventType = null;
              }
            }
          }
        }
      } catch (error) {
        if (smoothInterval) { clearInterval(smoothInterval); smoothInterval = null; }
        if (error.name === 'AbortError') {
          console.log('Stream was cancelled by user');
          this.currentMessages[msgIndex].streaming = false;
          if (!this.currentMessages[msgIndex].content) {
            this.currentMessages[msgIndex].content = '(Generation stopped)';
          }
        } else {
          console.error('Streaming error:', error);
          // Preserve partial text and append styled inline error
          const partialText = fullText ? `<div class="markdown-body" style="margin-bottom:10px;">${fullText}</div>` : '';
          const errorDetail = error.message || 'Connection lost';
          const errorHtml = `${partialText}<div class="inline-error-message"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:2px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><div><p style="margin:0;font-weight:500;">Something went wrong</p><p style="margin:4px 0 0;font-size:13px;">Failed to get a response. Please try again.</p><p style="margin-top:6px;font-size:13px;opacity:0.85;">${errorDetail}</p></div></div>`;
          this.currentMessages[msgIndex].html_content = errorHtml;
          this.currentMessages[msgIndex].content = fullText || '';
          this.currentMessages[msgIndex].streaming = false;
        }
      } finally {
        if (smoothInterval) clearInterval(smoothInterval);
        this.isStreaming = false;
        this.streamController = null;
        if (this.$refs.chatFooter && this.$refs.chatFooter.enableInput) {
          this.$refs.chatFooter.enableInput();
        }
      }
    },

    stopStreaming() {
      if (this.streamController) {
        this.streamController.abort();
      }
    },
 
    startConversationWithPrompt(prompt) {
      console.log('starting conversation with prompt:', prompt);
      this.sendMessage(prompt);
    },
 
    selectChatbot(bot) {
      // Skip reset if the same chatbot is already selected.
      // ChatbotDropdown emits select-chatbot on mount (immediate watcher),
      // which would otherwise wipe currentConversation when navigating
      // back from ProjectView.
      if (this.selectedChatbot && this.selectedChatbot.id === bot.id) {
        return;
      }
      this.selectedChatbot = bot;
      // Preserve pending project state when switching chatbots
      // (user may switch chatbot before sending first message in a project chat)
      const preserveProject = this.pendingProjectId;
      const preserveProjectName = this.pendingProjectName;
      this.currentConversation = null;
      this.isNewConversation = true;
      this.currentMessages = [];
      if (preserveProject) {
        this.pendingProjectId = preserveProject;
        this.pendingProjectName = preserveProjectName;
      }
    },

    onAutoTtsChanged(value) {
      this.autoTtsOutput = value;
    },

    async submitFeedback(messageResourceId, vote) {
      try {
        const res = await axios.get('/api/get-csrf-token');
        const token = res.data.csrfToken;
        const response = await axios.post(
          `/api/message/${messageResourceId}/feedback/`,
          { vote },
          { headers: { 'X-CSRFToken': token } }
        );
        // Update local message state
        const msg = this.currentMessages.find(m => m.resource_id === messageResourceId);
        if (msg) {
          msg.feedback_vote = response.data.feedback_vote;
        }
      } catch (error) {
        console.error('Error submitting feedback:', error);
      }
    },

    handleLogin() {
      this.isLoggedIn = true; // Set login state to true
      window.location.href = '/accounts/microsoft/login/';
    },
    handleLogout() {
      window.location.href = "/logout/";
      this.isLoggedIn = false; // Set login state to false
    },
  },
 
};
</script>
 
<style scoped>
html,
body{
  margin: 0;
  padding: 0;
  /* overflow: hidden; */
}
 
.app {
  display: flex;
  height: 100vh;
  overflow: hidden;
  /* Prevent horizontal scrolling */
}

.main-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: calc(100% - 20rem);
  height: 100%;
  /* Subtract the sidebar width */
  min-width: 0;
  /* Ensure the main content can shrink if necessary */
  overflow: hidden;
  /* Hide any overflow */
  flex-grow: 1;
  /* Allow main content to grow and shrink as needed */
  padding-top: 50px;
}
 
.dropdown-container {
  position: absolute;
  top: 0px;
  left: calc(20rem + 20px);
  display: flex;
  align-items: center;
  gap: 8px;
}

.project-indicator {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  margin-top: 10px;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-secondary, #666);
  background: var(--bg-tertiary, #f0f0f0);
  border-radius: 12px;
  white-space: nowrap;
}
 
.collapsed-dropdown {
  left: calc(60px + 20px);
}
 
/* Stop generating button */
.stop-generating-container {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  width: 100%;
}

.stop-generating-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #ffffff;
  border: 1px solid #d0d0d0;
  border-radius: 20px;
  color: #555;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.stop-generating-btn:hover {
  background: #f5f5f5;
  border-color: #999;
  color: #333;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.stop-generating-btn svg {
  flex-shrink: 0;
}

.header-container {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  font-family: TT Ramillas Trl, Inter, sans-serif;
}

/* Artifacts toggle button */
.artifacts-toggle-btn {
  position: fixed;
  top: 12px;
  right: 16px;
  z-index: 50;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.artifacts-toggle-btn:hover {
  background: #f5f5f5;
  color: #333;
  border-color: #ccc;
}

.artifacts-toggle-btn.active {
  background: #eef2ff;
  color: var(--accent-color, #2563eb);
  border-color: var(--accent-color, #2563eb);
}
</style>
 